/**
 * SunoModelsPanel — read-only list of the models your Suno account can use, custom models first.
 *
 * Reads Suno's billing info through the app's existing authenticated proxy (the same call as Test
 * Connection) and shows only the model entries: names, ids, keys. Nothing is changed or saved. It exists
 * to surface each custom model's custom_model_id and external_key, which Suno's page code uses but does
 * not reveal in static form.
 */

import React, { useState } from 'react';
import { Copy, Check, CloudDownload } from 'lucide-react';
import { getSunoCredits } from '@services/sunoApi';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { copyToClipboard } from '@/utils/clipboard';
import { extractSunoModels, describeResponseShape, type SunoModelEntry } from '@/shared/sunoModelList';

interface SunoModelsPanelProps {
  /** The Suno session cookie / token already saved in Suno Sync. */
  cookie: string;
}

type Status = 'idle' | 'loading' | 'done' | 'error';

const CopyField: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={() => { copyToClipboard(value); setDone(true); setTimeout(() => setDone(false), 1400); }}
      className="group flex items-center gap-1.5 text-left"
      title={`Copy ${label}`}
      aria-label={`Copy ${label} ${value}`}
    >
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
      <code className="text-[11px] text-slate-300 break-all">{value}</code>
      {done ? <Check size={11} className="text-green-400 shrink-0" /> : <Copy size={11} className="text-slate-600 group-hover:text-accent shrink-0" />}
    </button>
  );
};

const ModelRow: React.FC<{ m: SunoModelEntry }> = ({ m }) => (
  <li className="rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 space-y-1" data-testid={`suno-model-${m.externalKey}`}>
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-semibold text-slate-100">{m.name}</span>
      {m.majorVersion && <Badge variant="default-muted" size="xs">{m.majorVersion}</Badge>}
      {m.isCustom && m.ownership && <Badge variant="info-muted" size="xs">{m.ownership}</Badge>}
      {m.isDefault && <Badge variant="primary" size="xs">default</Badge>}
      {m.canUse === false && <Badge variant="warning-muted" size="xs">can't use</Badge>}
    </div>
    <div className="flex flex-col gap-0.5">
      {m.id && <CopyField label={m.isCustom ? 'custom_model_id' : 'id'} value={m.id} />}
      <CopyField label="external_key" value={m.externalKey} />
      {m.baseModel && <CopyField label="base model" value={m.baseModel} />}
    </div>
  </li>
);

export const SunoModelsPanel: React.FC<SunoModelsPanelProps> = ({ cookie }) => {
  const [status, setStatus] = useState<Status>('idle');
  const [models, setModels] = useState<SunoModelEntry[]>([]);
  const [shape, setShape] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [jsonCopied, setJsonCopied] = useState(false);

  const load = async () => {
    setStatus('loading');
    setError('');
    try {
      const response = await getSunoCredits(cookie);
      const found = extractSunoModels(response);
      setModels(found);
      setShape(found.length === 0 ? describeResponseShape(response) : []);
      setStatus('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read your Suno models.');
      setStatus('error');
    }
  };

  const custom = models.filter(m => m.isCustom);
  const standard = models.filter(m => !m.isCustom);

  const copyCustomJson = () => {
    const payload = custom.map(({ name, id, externalKey, baseModel, ownership, canUse }) => ({ name, id, externalKey, baseModel, ownership, canUse }));
    copyToClipboard(JSON.stringify(payload, null, 2));
    setJsonCopied(true);
    setTimeout(() => setJsonCopied(false), 1600);
  };

  return (
    <div className="space-y-3 pt-2">
      <div className="space-y-0.5">
        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest ml-1">Your Suno models</label>
        <p className="text-[11px] text-slate-500 ml-1">
          Read-only. Lists the models on your Suno account, custom ones first, with the ids and keys. Nothing is changed or saved.
        </p>
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={load}
        disabled={!cookie || status === 'loading'}
        isLoading={status === 'loading'}
        loadingText="Loading..."
        leftIcon={<CloudDownload size={14} />}
        className="font-black text-[11px] uppercase tracking-widest"
      >
        {status === 'done' ? 'Reload my Suno models' : 'Load my Suno models'}
      </Button>
      {!cookie && <p className="text-[11px] text-amber-400 ml-1">Add and save your Suno token above first.</p>}
      {status === 'error' && <p className="text-[11px] text-red-400 font-bold ml-1 italic">{error}</p>}

      {status === 'done' && models.length === 0 && (
        <div className="rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 space-y-1">
          <p className="text-xs text-slate-300">No model entries were found in Suno's response.</p>
          <p className="text-[11px] text-slate-500">
            The response contains these top-level fields (names only, no values), which tells us where to look next:
          </p>
          <code className="block text-[11px] text-slate-400 break-words">{shape.join(', ') || '(empty)'}</code>
        </div>
      )}

      {status === 'done' && models.length > 0 && (
        <div className="space-y-3">
          <section aria-label="Custom models" className="space-y-1.5">
            <div className="flex items-center justify-between">
              <h4 className="text-[11px] font-black uppercase tracking-widest text-amber-300">Custom models ({custom.length})</h4>
              {custom.length > 0 && (
                <button type="button" onClick={copyCustomJson} className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-accent transition-colors">
                  {jsonCopied ? <Check size={11} className="text-green-400" /> : <Copy size={11} />}
                  {jsonCopied ? 'Copied' : 'Copy as JSON'}
                </button>
              )}
            </div>
            {custom.length === 0
              ? <p className="text-[11px] text-slate-500">No custom models in the response.</p>
              : <ul className="space-y-1.5">{custom.map(m => <ModelRow key={`${m.id}|${m.externalKey}`} m={m} />)}</ul>}
          </section>

          <section aria-label="Standard models" className="space-y-1.5">
            <h4 className="text-[11px] font-black uppercase tracking-widest text-slate-400">Standard models ({standard.length})</h4>
            {standard.length === 0
              ? <p className="text-[11px] text-slate-500">No standard models in the response.</p>
              : <ul className="space-y-1.5">{standard.map(m => <ModelRow key={`${m.id}|${m.externalKey}`} m={m} />)}</ul>}
          </section>
        </div>
      )}
    </div>
  );
};
